import React, { useState } from 'react';
import '../MortgageCalculator.css';

const RefinanceCalculator: React.FC = () => {
  const [currentLoanBalance, setCurrentLoanBalance] = useState<string>('250000');
  const [currentRate, setCurrentRate] = useState<string>('7.0');
  const [currentTerm, setCurrentTerm] = useState<string>('30');
  const [currentTermRemaining, setCurrentTermRemaining] = useState<string>('25');
  const [newRate, setNewRate] = useState<string>('5.5');
  const [newTerm, setNewTerm] = useState<string>('30');
  const [closingCosts, setClosingCosts] = useState<string>('5000');
  const [results, setResults] = useState<{
    monthlySavings: number;
    breakEvenMonths: number;
    lifetimeSavings: number;
  } | null>(null);

  const calculateRefinance = () => {
    // Current loan details
    const balance = parseFloat(currentLoanBalance);
    const currentMonthlyRate = parseFloat(currentRate) / 100 / 12;
    const remainingMonths = parseFloat(currentTermRemaining) * 12;
    
    // Calculate current monthly payment
    const currentMonthlyPayment = 
      (balance * currentMonthlyRate * Math.pow(1 + currentMonthlyRate, remainingMonths)) / 
      (Math.pow(1 + currentMonthlyRate, remainingMonths) - 1);
    
    // New loan details
    const newMonthlyRate = parseFloat(newRate) / 100 / 12;
    const newLoanMonths = parseFloat(newTerm) * 12;
    const costs = parseFloat(closingCosts);
    
    // Calculate new monthly payment
    const newMonthlyPayment = 
      (balance * newMonthlyRate * Math.pow(1 + newMonthlyRate, newLoanMonths)) / 
      (Math.pow(1 + newMonthlyRate, newLoanMonths) - 1);
    
    // Calculate savings
    const monthlySavings = currentMonthlyPayment - newMonthlyPayment;
    const breakEvenMonths = costs / monthlySavings;
    
    // Calculate total lifetime savings
    const totalCurrentPayments = currentMonthlyPayment * remainingMonths;
    const totalNewPayments = newMonthlyPayment * newLoanMonths;
    const lifetimeSavings = totalCurrentPayments - (totalNewPayments + costs);
    
    setResults({
      monthlySavings,
      breakEvenMonths,
      lifetimeSavings
    });
  };

  return (
    <div className="calculator">
      <h2>Mortgage Refinance Calculator</h2>
      
      <div className="input-group">
        <label htmlFor="currentLoanBalance">Current Loan Balance ($)</label>
        <input
          id="currentLoanBalance"
          type="number"
          value={currentLoanBalance}
          onChange={(e) => setCurrentLoanBalance(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="currentRate">Current Interest Rate (%)</label>
        <input
          id="currentRate"
          type="number"
          step="0.01"
          value={currentRate}
          onChange={(e) => setCurrentRate(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="currentTerm">Original Loan Term (years)</label>
        <input
          id="currentTerm"
          type="number"
          value={currentTerm}
          onChange={(e) => setCurrentTerm(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="currentTermRemaining">Remaining Term (years)</label>
        <input
          id="currentTermRemaining"
          type="number"
          value={currentTermRemaining}
          onChange={(e) => setCurrentTermRemaining(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="newRate">New Interest Rate (%)</label>
        <input
          id="newRate"
          type="number"
          step="0.01"
          value={newRate}
          onChange={(e) => setNewRate(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="newTerm">New Loan Term (years)</label>
        <input
          id="newTerm"
          type="number"
          value={newTerm}
          onChange={(e) => setNewTerm(e.target.value)}
        />
      </div>
      
      <div className="input-group">
        <label htmlFor="closingCosts">Closing Costs ($)</label>
        <input
          id="closingCosts"
          type="number"
          value={closingCosts}
          onChange={(e) => setClosingCosts(e.target.value)}
        />
      </div>
      
      <button onClick={calculateRefinance}>Calculate Savings</button>
      
      {results && (
        <div className="result">
          <h3>Refinance Results</h3>
          <p>Monthly Savings: ${results.monthlySavings.toFixed(2)}</p>
          <p>Break-even Point: {Math.round(results.breakEvenMonths)} months</p>
          <p>Lifetime Savings: ${Math.round(results.lifetimeSavings).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
};

export default RefinanceCalculator;